import React, {useState, useEffect} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import {useRouting} from '../hooks/useRouting';
import {useTranslation} from 'react-i18next';
import {spacing} from '../constants';
import {MATERIAL_ICONS_FONT} from '../styles/fonts';
import LegalContent from '../components/LegalContent';
import {useDocumentMeta} from '../hooks/useDocumentMeta';

type Tab = 'privacy' | 'terms';

const VALID_TABS: Tab[] = ['privacy', 'terms'];

export default function LegalScreen() {
  const {t, i18n} = useTranslation();
  const {pathname, navigateTo} = useRouting();
  const {width} = useWindowDimensions();
  const isMobile = width < 768;

  // Get the page from the URL path (e.g., /privacy -> privacy)
  const page = pathname.replace('/', '') as Tab;
  const initialTab: Tab = VALID_TABS.includes(page) ? page : 'terms';
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);

  const pageTitle =
    activeTab === 'privacy'
      ? "Privacy Policy – Oma's App"
      : "Terms of Service – Oma's App";
  useDocumentMeta(pageTitle, undefined, i18n.language, `/${activeTab}`);

  useEffect(() => {
    if (VALID_TABS.includes(page)) {
      setActiveTab(page);
    }
  }, [page]);

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    navigateTo(`/${tab}`, {replace: true});
  };

  const tabs: {key: Tab; label: string}[] = [
    {key: 'privacy', label: t('home.privacy.title')},
    {key: 'terms', label: t('home.terms.title')},
  ];

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={[styles.header, isMobile && styles.headerMobile]}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigateTo('/')}
          activeOpacity={0.8}>
          <Text style={styles.backIcon}>arrow_back</Text>
          <Text style={styles.backText}>{t('common.back')}</Text>
        </TouchableOpacity>
      </View>

      {/* Tabs */}
      <View style={[styles.tabContainer, isMobile && styles.tabContainerMobile]}>
        {tabs.map(tab => (
          <TouchableOpacity
            key={tab.key}
            style={[styles.tab, activeTab === tab.key && styles.tabActive]}
            onPress={() => handleTabChange(tab.key)}
            activeOpacity={0.8}>
            <Text
              style={[
                styles.tabText,
                activeTab === tab.key && styles.tabTextActive,
              ]}>
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Content */}
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          !isMobile && styles.scrollContentDesktop,
        ]}
        showsVerticalScrollIndicator={true}>
        <LegalContent type={activeTab} />

        {/* Last updated */}
        <Text style={styles.lastUpdated}>
          {activeTab === 'privacy'
            ? t('home.privacy.lastUpdated', {date: '2026-01-27'})
            : t('home.terms.lastUpdated', {date: '2026-01-27'})}
        </Text>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fffaf5',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  },
  headerMobile: {
    padding: spacing.md,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  backIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: '#ee8c2b',
  },
  backText: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 16,
    fontWeight: '500',
    color: '#ee8c2b',
  },
  tabContainer: {
    flexDirection: 'row',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
    gap: spacing.sm,
    flexWrap: 'wrap',
  },
  tabContainerMobile: {
    paddingHorizontal: spacing.md,
  },
  tab: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    backgroundColor: 'transparent',
  },
  tabActive: {
    backgroundColor: '#ee8c2b',
  },
  tabText: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
  },
  tabTextActive: {
    color: '#fff',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: spacing.lg,
  },
  scrollContentDesktop: {
    maxWidth: 800,
    alignSelf: 'center',
    width: '100%',
  },
  lastUpdated: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 12,
    color: '#999',
    marginTop: spacing.xl,
    textAlign: 'center',
  },
});
